import { audioMixerHtml } from '../ui/audio-mixer.js';
import { escapeHtml, safeColour } from '../ui/html.js';
import { LOCALES, locale, t } from '../i18n/index.js';

export type MenuStep = 'language' | 'driver' | 'garage' | 'play' | 'lobby';

/** Rail symbols: touch layouts show these instead of step numbers. */
const STEP_ICONS = {
  language: '<svg viewBox="0 0 24 24"><path d="M4 5.5h9M8.5 3.5v2M6 5.5c.6 3.6 3 6.4 6.5 7.6M11 5.5c-.6 3.6-3 6.4-6.5 7.6"/><path d="m12 20.5 4-9.5 4 9.5M13.4 17.3h5.2"/></svg>',
  driver: '<svg viewBox="0 0 24 24"><path d="M4 13.5a8 8 0 0 1 16 0V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z"/><path d="M4.5 12h8.5a2 2 0 0 1 2 2v1H20"/></svg>',
  garage: '<svg viewBox="0 0 24 24"><path d="M3.5 15.5v-2.8l2.2-4.4A2 2 0 0 1 7.5 7h9a2 2 0 0 1 1.8 1.3l2.2 4.4v2.8a1.5 1.5 0 0 1-1.5 1.5h-15a1.5 1.5 0 0 1-1.5-1.5Z"/><path d="M4 12.5h16M7 17v1.5M17 17v1.5"/></svg>',
  play: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.6 2.4 3.6 5.2 3.6 8.5s-1 6.1-3.6 8.5c-2.6-2.4-3.6-5.2-3.6-8.5s1-6.1 3.6-8.5Z"/></svg>',
  lobby: '<svg viewBox="0 0 24 24"><circle cx="9" cy="9" r="3"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0"/><circle cx="17" cy="9.5" r="2.4"/><path d="M15.5 14.2A4.5 4.5 0 0 1 21 18.5"/></svg>',
  race: '<svg viewBox="0 0 24 24"><path d="M5 21V4"/><path d="M5 4h13l-2.5 4L18 12H5"/><path d="M9.5 4v8M14 4v8M5 8h11.5"/></svg>',
} as const;

const STEPS: readonly { id: MenuStep | 'race'; label: string; hint: string }[] = [
  { id: 'language', label: 'LANGUAGE', hint: '' },
  { id: 'driver', label: 'DRIVER', hint: 'Name + steering' },
  { id: 'garage', label: 'GARAGE', hint: 'Car + livery' },
  { id: 'play', label: 'PLAY', hint: 'Join · host · offline' },
  { id: 'lobby', label: 'LOBBY', hint: 'Track + grid' },
  { id: 'race', label: 'RACE', hint: 'Lights out' },
];

export interface MenuFrameOptions {
  step: MenuStep;
  /** Steps the player may jump to from the rail. */
  reachable: ReadonlySet<MenuStep>;
  driver: { name: string; carName: string; paint: string; level: number } | null;
  body: string;
  footer: string;
  online: boolean;
  installAvailable: boolean;
  /** Extra class for step-specific layouts. */
  variant?: string;
  /** GRAPHICS popover (performance test + preset), pre-rendered by the owner. */
  graphicsQuick?: string;
}

/**
 * Shared chrome for every wizard step: backdrop, top bar with the step rail
 * and driver chip, one scrollable stage and a sticky action footer. Keeping
 * the frame identical across steps makes the flow read as one guided journey.
 */
export function menuFrameHtml(options: MenuFrameOptions): string {
  return `
    <main class="wizard-shell ${options.variant ?? ''}" data-step="${options.step}">
      <div class="menu-backdrop" aria-hidden="true"></div>
      <div class="wizard-vignette" aria-hidden="true"></div>
      <header class="wizard-topbar">
        <a class="wordmark" href="/" data-home><span class="wordmark-slash">//</span><span>NIGHTSHIFT</span></a>
        ${wizardRailHtml(options.step, options.reachable)}
        <div class="wizard-top-actions">
          <details class="notif-center" id="notif-center"><summary title="${t('Race control log')}"><i aria-hidden="true"></i>${t('LOG')}<b id="notif-count" hidden></b></summary><div class="notif-panel" role="log" aria-label="${t('Recent race control messages')}" id="notif-list"></div></details>
          <details class="audio-quick" id="audio-quick"><summary title="${t('Sound and music')}" aria-label="${t('Sound and music')}"><i class="audio-speaker" aria-hidden="true"></i><span>${t('SOUND')}</span></summary><div class="audio-quick-panel">${audioMixerHtml('compact')}</div></details>
          ${options.graphicsQuick ?? ''}
          <button class="install-app" id="install-app" ${options.installAvailable ? '' : 'hidden'}>${t('INSTALL ↘')}</button>
          ${options.step === 'language' ? '' : languageSwitchHtml()}
          <span class="network-badge" data-online="${options.online}" title="${options.online ? t('ONLINE') : t('OFFLINE READY')}"><i></i>${options.online ? t('ONLINE') : t('OFFLINE READY')}</span>
          ${options.driver ? `<button type="button" class="driver-chip" data-wizard-step="driver" title="${t('Edit driver profile')}"><i style="--driver-colour:${safeColour(options.driver.paint)}">${escapeHtml(options.driver.name.slice(0, 1).toUpperCase())}</i><span><b>${escapeHtml(options.driver.name)}</b><small>${t('LV {level} · {car}', { level: String(options.driver.level).padStart(2, '0'), car: escapeHtml(options.driver.carName) })}</small></span></button>` : ''}
        </div>
      </header>
      <section class="wizard-stage" id="wizard-stage">${options.body}</section>
      <footer class="wizard-footer">${options.footer}</footer>
      <p class="form-error wizard-error" id="form-error" role="alert"></p>
      <div class="toast-stack" id="toast-stack" role="status" aria-live="polite"></div>
    </main>`;
}

/** The step rail on its own, so a language preview can repaint it in place. */
export function wizardRailHtml(current: MenuStep, reachableSteps: ReadonlySet<MenuStep>): string {
  const activeIndex = STEPS.findIndex((step) => step.id === current);
  return `
        <nav class="wizard-rail" aria-label="${t('Setup progress')}">
          <ol>
            ${STEPS.map((step, index) => {
              const state = index < activeIndex ? 'done' : index === activeIndex ? 'current' : 'next';
              const reachable = step.id !== 'race' && reachableSteps.has(step.id) && step.id !== current;
              const content = `<b><em class="rail-num">${index < activeIndex ? '✓' : index + 1}</em><i class="rail-icon" aria-hidden="true">${STEP_ICONS[step.id]}</i></b><span>${t(step.label)}<small>${step.id === 'language' ? currentLanguageName() : t(step.hint)}</small></span>`;
              return `<li data-state="${state}" data-rail="${step.id}">${reachable
                ? `<button type="button" data-wizard-step="${step.id}" title="${t(`Go to ${step.label.toLowerCase()}`)}">${content}</button>`
                : `<span class="wizard-rail-item" ${state === 'current' ? 'aria-current="step"' : ''}>${content}</span>`}</li>`;
            }).join('')}
          </ol>
        </nav>
`;
}

function currentLanguageName(): string {
  return LOCALES.find((option) => option.id === locale())?.native ?? '';
}

/** Two-way DE | EN toggle; the frame owner re-renders on change. */
function languageSwitchHtml(): string {
  return `<div class="language-switch" role="group" aria-label="${t('Language')}">${LOCALES.map((option) => `<button type="button" data-set-language="${option.id}" lang="${option.id}" aria-pressed="${option.id === locale()}" title="${option.native}">${option.id.toUpperCase()}</button>`).join('')}</div>`;
}

export function wizardFooterHtml(options: {
  back?: { label: string; step?: MenuStep; action?: string } | null;
  next?: { label: string; action: string; disabled?: boolean; hint?: string } | null;
  hint?: string;
}): string {
  return `
    <div class="wizard-footer-inner">
      ${options.back ? `<button type="button" class="wizard-back" ${options.back.step ? `data-wizard-step="${options.back.step}"` : `data-wizard-action="${options.back.action ?? 'back'}"`} data-pad-back data-pad-back-label="${t('BACK')}"><b aria-hidden="true">←</b>${escapeHtml(options.back.label)}</button>` : '<span></span>'}
      <p class="wizard-hint">${options.hint ? escapeHtml(options.hint) : ''}</p>
      ${options.next ? `<button type="button" class="wizard-next" data-wizard-action="${options.next.action}" data-pad-default data-pad-start data-pad-verb="${t('NEXT')}" ${options.next.disabled ? 'disabled' : ''} ${options.next.hint ? `title="${escapeHtml(options.next.hint)}"` : ''}>${escapeHtml(options.next.label)}<b aria-hidden="true">→</b></button>` : '<span></span>'}
    </div>`;
}
